import * as React from 'react';
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Table, TableCell, TableContainer, TableRow, TableHead, TableBody, TextField, FormControl, Tooltip } from '@mui/material';
import { DataGridPro, koKR, enUS, zhCN } from '@mui/x-data-grid-pro';

import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import PageInfo from 'components/PageInfo';
import queryString from 'query-string';
import { useState } from 'react';
import { useEffect } from 'react';
import { getSensorsByQuery, getSensorsByQueryString } from 'components/axios/iotAxios';
import dayjs from "utils/dayjs";
import { useEnumApi } from 'contexts/EnumApiContext';
import CustomEnumSelect from 'components/select/CustomEnumSelect';
import { BootstrapInput } from 'data/data';
import { fromServerFormat } from 'components/date_picker/dateUtils';
import useConfig from 'hooks/useConfig';

const muiLocaleMap = {
  ko: koKR.components.MuiDataGrid.defaultProps.localeText,
  en: enUS.components.MuiDataGrid.defaultProps.localeText,
  'zh-CN': zhCN.components.MuiDataGrid.defaultProps.localeText,
};

const SensorSetting = () => {
    const { getEnumValueByEnumNameAndCode, getEnumValueByEnumName } = useEnumApi();
    const { t, i18n } = useTranslation();
    const { mode } = useConfig();

    const navigate = useNavigate();
    const [searchField, setSearchField] = useState('sn');
    const [searchSensorType, setSearchSensorType] = useState('');
    const [searchCommunicationType, setSearchCommunicationType] = useState('');
    const [searchKeyword, setSearchKeyword] = useState('');
    const [pageInfo, setPageInfo] = useState({});
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(process.env.REACT_APP_DEFAULT_PAGE_SIZE);
    const [sensors, setSensors] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isParam, setIsParam] = useState(false);
    // const [searchFields] = useState([{code:"sn", title:"S/N"}, {code:"no", title:t('message.common.subtitle.equipment.number')}]);
    const searchFields = [{code:"sn", title:"S/N"}, {code:"wifiMacAdr", title:"WIFI MAC"}, {code:"bleMacAdr", title:"BLE MAC"}
      , {code:"canId", title:"CAN ID"}, {code:"stationName", title:t('message.common.subtitle.company.name')}
      , {code:"equipmentName", title:t('message.common.subtitle.equipment.name')}, {code:"madeBy", title:t('message.common.subtitle.manufacturing.company')}
    ];

    const [sortModel, setSortModel] = useState(null);
    const [searchParam, setSearchParam] = useState({});
    const [searchParamString, setSearchParamString] = useState('');

    // const connectProblemDay = dayjs.tz(process.env.REACT_APP_TIMEZONE).subtract(7, "day").format(t('message.common.text.date.format.date'));
    const qs = queryString.parse(window.location.search);
    // useMemo를 사용해 컴포넌트가 렌더링될 때 한 번만 계산
    const connectProblemDay = React.useMemo(() => {
        const tz = process.env.REACT_APP_TIMEZONE || "Asia/Seoul";
        const fmt = t('message.common.text.date.format.date');
        
        // fmt가 유효한지 확인 후 처리
        const day = dayjs().tz(tz).subtract(7, "day");
        return day.isValid() ? day.format(fmt) : day.format("YYYY-MM-DD");
    }, [t]);
    
    const columns = [
        { field: 'no', headerName: 'No.', sortable: false, width: 80
          , renderCell:(params)=>(<Link to={'/setting/iot/sensor/'+params.row?.id+searchParamString}>{params.row?.no}</Link>)
        },
        { field: 'sensorType', headerName: t('message.common.subtitle.sensor.type'), sortable:true, width: 100
          , renderCell:(params)=>(<Link to={'/setting/iot/sensor/'+params.row?.id+searchParamString}>{params.row?.sensorType 
            && getEnumValueByEnumNameAndCode("SensorType", params.row?.sensorType)?.[0]?.title}</Link>)
        },
        { field: 'sn', headerName: 'S/N', sortable:true, width: 150
          , renderCell:(params)=>(<Link to={'/setting/iot/sensor/'+params.row?.id+searchParamString}>{params.row?.sn}</Link>)
        },
        { field: 'wifiMacAdr', headerName: 'WIFI MAC', sortable:true, width: 150
          , renderCell:(params)=>(<Link to={'/setting/iot/sensor/'+params.row?.id+searchParamString}>{params.row?.wifiMacAdr}</Link>)
        },
        { field: 'bleMacAdr', headerName: 'BLE MAC', sortable:true, width: 150
          , renderCell:(params)=>(<Link to={'/setting/iot/sensor/'+params.row?.id+searchParamString}>{params.row?.bleMacAdr}</Link>)
        },
        { field: 'canId', headerName: 'CAN ID', sortable:true, width: 100
          , renderCell:(params)=>(<Link to={'/setting/iot/sensor/'+params.row?.id+searchParamString}>{params.row?.canId}</Link>)
        },
        { field: 'equipmentPart.equipment.managementCompany.info.name', headerName: t('message.common.subtitle.installation.location'), sortable:false, width: 300
          , renderCell:(params)=>(<Link to={'/setting/iot/sensor/'+params.row?.id+searchParamString}>
            {params.row?.equipment?.managementCompany?.info?.name && <Tooltip title={<div style={{color:'#000'}}>{params.row?.equipment?.managementCompany?.info?.address}</div>}>
            <span>{params.row?.equipment?.managementCompany?.info?.name}</span></Tooltip>}
            {params.row?.equipment?.name && ' > '+params.row?.equipment?.name}
            {params.row?.equipmentPart?.masterEquipmentPart?.name && ' > '+params.row?.equipmentPart?.masterEquipmentPart?.name}
            {params.row?.equipmentPart?.componentLocation?.name && ' > '+params.row?.equipmentPart?.componentLocation?.name}
            </Link>)
        },
        { field: 'madeBy', headerName: t('message.common.subtitle.manufacturing.company'), sortable:true, width: 100
          , renderCell:(params)=>(<Link to={'/setting/iot/sensor/'+params.row?.id+searchParamString}>{params.row?.madeBy}</Link>)
            // && getEnumValueByEnumNameAndCode("GatewayMF", params.row?.madeBy)?.[0]?.title}</Link>)
        },
        { field: 'aliveStatus', headerName: t('message.common.subtitle.live.status'), sortable:true, width: 100
          , renderCell:(params)=>(<Link to={'/setting/iot/sensor/'+params.row?.id+searchParamString}><Tooltip title={<div style={{color:'#000'}}>{fromServerFormat(params.row?.latestAliveStatusCheckedAt, {format:t('message.common.text.date.format.datetimess')})}</div>}><span>{params.row?.aliveStatus}</span></Tooltip></Link>) 
        },
        { field: 'axial', headerName: 'X, Y, Z', sortable:false, width: 120
          , renderCell:(params)=>(<Link to={'/setting/iot/sensor/'+params.row?.id+searchParamString}>
            {params.row?.axial && getEnumValueByEnumNameAndCode("SensorDirection", params.row?.axial)?.[0]?.title}
            {params.row?.radial && ', '+getEnumValueByEnumNameAndCode("SensorDirection", params.row?.radial)?.[0]?.title}
            {params.row?.vertical && ', '+getEnumValueByEnumNameAndCode("SensorDirection", params.row?.vertical)?.[0]?.title}
          </Link>)
        },
        { field: 'commType', headerName: t('message.common.subtitle.communication.type'), sortable:true, width: 100
          , renderCell:(params)=>(<Link to={'/setting/iot/sensor/'+params.row?.id+searchParamString}>{params.row?.sensorType 
            && getEnumValueByEnumNameAndCode("CommunicationType", params.row?.commType)?.[0]?.title}</Link>)
        },
        { field: 'batLevel', headerName: t('message.common.subtitle.battery'), sortable:true, width: 100
          , renderCell:(params)=>(<Link to={'/setting/iot/sensor/'+params.row?.id+searchParamString}><Tooltip title={<div style={{color:'#000'}}>{fromServerFormat(params.row?.batChkedAt, {format:t('message.common.text.date.format.datetimess')})}</div>}><span>{params.row?.batLevel}</span></Tooltip></Link>) 
        },
        { field: 'opStatus', headerName: t('message.common.subtitle.operating.status'), sortable:true, width: 100
          , renderCell:(params)=>(<Link to={'/setting/iot/sensor/'+params.row?.id+searchParamString}><Tooltip title={<div style={{color:'#000'}}>{fromServerFormat(params.row?.latestOpStatusCheckedAt, {format:t('message.common.text.date.format.datetimess')})}</div>}><span>{params.row?.opStatus}</span></Tooltip></Link>) 
        },
        { field: 'latestCommedAt', headerName: t('message.common.subtitle.last.communication'), sortable:true, width: 200
          , renderCell:(params)=>(<div 
            className={params.row?.latestCommedAt !== null ? (params.row?.latestCommedAt < connectProblemDay ? 'red' : 'black') : 'red'}
            ><Link to={'/setting/iot/sensor/'+params.row?.id+searchParamString}>{fromServerFormat(params.row?.latestCommedAt, {format:t('message.common.text.date.format.datetimess')})}</Link></div>)
        },
    ];

    const handlePaging = (value) => {
      value = isNaN(value) ? 1 : value;
      setCurrentPage(value-1);
      setIsLoading(false);

      let searchParamCopy = {...searchParam};
      searchParamCopy.page = (value-1);
      setSearchParam(searchParamCopy);
    }

    useEffect(()=>{
        const searchOrders = searchParam.searchOrder;
        const searchParamCopy = {...searchParam};
        delete searchParamCopy.searchOrder;
        let searchString = Object.entries(searchParamCopy).map(([key, value]) => ( key+'='+value)).filter(v=>v).join('&');
        if (searchOrders && searchOrders.length > 0) {
          let sortString = '';
          searchOrders.forEach(order=>sortString+="&sort="+order.sort)
          searchString += sortString;
        }
        setSearchParamString("?"+searchString)
    }, [searchParam])
    
    useEffect(()=>{
        const searchParamCopy = {...searchParam};
        let searchOrders = [];
        if (sortModel && sortModel.length > 0) {
          searchOrders.push({sort : sortModel[0].field+","+sortModel[0].sort});
        }
        searchParamCopy.searchOrder = searchOrders;
        setSearchParam(searchParamCopy)
    },[sortModel])

    useEffect(()=> {
        if (qs) {
            if (qs.page && qs.page !== (currentPage)) {setCurrentPage(qs.page);}
            if (qs.size) setPageSize(qs.size);
            if (qs.searchField) setSearchField(qs.searchField);
            if (qs.searchKeyword) setSearchKeyword(qs.searchKeyword);
            if (qs.searchSensorType) setSearchSensorType(qs.searchSensorType);
            if (qs.searchCommunicationType) setSearchCommunicationType(qs.searchCommunicationType);

            let searchParamCopy = {...searchParam};
            if (qs.page && qs.page !== (currentPage)) {searchParamCopy.page = qs.page};
            if (qs.size) {searchParamCopy.size = qs.size} ;
            if (qs.searchField) {searchParamCopy.searchField = qs.searchField};
            if (qs.searchKeyword) {searchParamCopy.searchKeyword = qs.searchKeyword};
            if (qs.searchSensorType) {searchParamCopy.searchSensorType = qs.searchSensorType};
            if (qs.searchCommunicationType) {searchParamCopy.searchCommunicationType = qs.searchCommunicationType};
            if (qs.sort && qs.sort.length > 0) {
                let orders = [];
                let sortModel = [];
                if (!Array.isArray(qs.sort)) {
                  orders.push({sort:qs.sort});
                  sortModel.push({"field": qs.sort.split(',')[0],"sort": qs.sort.split(',')[1]})
                } else {
                  (qs.sort).forEach(order=>orders.push({sort:order}));
                  qs.map(order=>sortModel.push({"field": order.sort.split(',')[0],"sort": order.sort.split(',')[1]}))
                }
                setSortModel(sortModel)
                searchParamCopy.searchOrder = orders;
            } else {
                delete searchParamCopy.searchOrder
            };
            setSearchParam(searchParamCopy);
        }
        setIsParam(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [qs.page, qs.size, qs.searchField, qs.searchKeyword, qs.searchSensorType, qs.searchCommunicationType]);
    
    useEffect(()=> {
        if ((!isLoading || sortModel != null)  && !isNaN(currentPage) && isParam) {
            sensorsList();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, isLoading, isParam, sortModel]);

    const sensorsList = async(curPage=currentPage) => {
        isNaN(curPage) && setCurrentPage(0);
        isNaN(pageSize) && setPageSize(process.env.REACT_APP_DEFAULT_PAGE_SIZE);
        setCurrentPage(curPage)

        let param = {
            page : curPage,
            size : pageSize
        };
        let keyword = searchKeyword;
        if (searchKeyword) {
          if (searchField &&
              (searchField === 'wifiMacAdr' || searchField === 'bleMacAdr' || searchField === 'canId' 
              || searchField === 'equipmentName' || searchField === 'madeBy' || searchField === 'stationName' || searchField === 'sn')) {
              keyword = 'contains('+searchKeyword+')';
          }

          if (searchField === 'wifiMacAdr') {
              param.wifiMacAdr = keyword;
          } else if (searchField === 'bleMacAdr') {
              param.bleMacAdr = keyword;
          } else if (searchField === 'canId') {
              param.canId = keyword;
          } else if (searchField === 'equipmentName') {
              param['equipmentPart.equipment.name'] = keyword;
          } else if (searchField === 'madeBy') {
              param.madeBy = keyword;
          } else if (searchField === 'stationName') {
              param['equipmentPart.equipment.managementCompany.info.name'] = keyword;
          } else if (searchField === 'sn') {
              param.sn = keyword;
          }
        }
        if (searchSensorType) {
          param.sensorType = searchSensorType;
        }
        if (searchCommunicationType) {
          param.commType = searchCommunicationType;
        }

        let addQueryString = "";
        if (sortModel && sortModel.length > 0)
          addQueryString += "&sort="+sortModel[0].field+","+sortModel[0].sort;
        else
          addQueryString += "";
        let queryString = Object.entries(param).map(([key, value]) => (key + '=' + value)).filter(v => v).join('&');
        queryString += addQueryString;
        await getSensorsByQueryString("?"+queryString).then((data)=>{
            if (data != null) {
                setPageInfo(data.page);
                if(data._embedded && data._embedded.sensors) {
                    let list = data._embedded.sensors;
                    list && list.map(function(sensor, i) {
                        sensor.idx = (i+1);
                        sensor.no = data.page.totalElements-((currentPage)*data.page.size)-i;
                    })

                    setSensors(list);
                } else {
                    setCurrentPage(0);
                    setSensors([]);
                }
            } else {
                pageInfo = {
                    size : pageInfo.size,
                    totalElements : 0,
                    totalPages : 0,
                    number : 0
                }
                setPageInfo(pageInfo);
                setSensors([]);
            }
        });
        setIsLoading(true);
    }

    const handleSelect = (event, value) => {
        event.preventDefault();
        setSearchField(value.props.value);
    };

    const handleInput = (event) => {
        event.preventDefault();
        setSearchKeyword(event.target.value.trim());
    };

    const handleOnKeyPress = e => {
    if (e.key === 'Enter') {
        handleSearch(e); // Enter 입력이 되면 클릭 이벤트 실행
    }
    };

    const handleSearch = (event) => {
        event.preventDefault();
        setCurrentPage(0);
        pageInfo.number = 0;
        setPageInfo(pageInfo);
        sensorsList(0);

        let searchParamCopy = {...searchParam};
        searchParamCopy.page = 0;
        if (pageSize) {searchParamCopy.size = pageSize};
        if (searchField) {searchParamCopy.searchField = searchField};
        if (searchSensorType) {searchParamCopy.searchSensorType = searchSensorType};
        if (searchCommunicationType) {searchParamCopy.searchCommunicationType = searchCommunicationType};
        // if (searchKeyword) {searchParamCopy.searchKeyword = searchKeyword};
        searchParamCopy.searchKeyword = searchKeyword;
        setSearchParam(searchParamCopy)
        const paramString = Object.entries(searchParamCopy).map(([key, value]) => ( key+'='+value)).filter(v=>v).join('&');
        // console.log(paramString)
        navigate("?"+paramString)
    };
    
    return (
    <div className='cal' style={{padding:"15px 0"}}>
      <div className='search_section'>
        <Box sx={{ minWidth: 120 }}>
        {isLoading && 
        <>
            {getEnumValueByEnumName("SensorType") && 
            <FormControl sx={{ m: 1, minWidth: 80 }}>
              <CustomEnumSelect
                key={searchSensorType}
                label={t('message.common.subtitle.sensor.type')}
                labelName={t('message.common.subtitle.sensor.type')}
                name={'searchSensorType'}
                defaultValue={searchSensorType}
                onChange={(e)=>{
                  setSearchSensorType(e.target.value);
                }}
                list={getEnumValueByEnumName("SensorType")}
            />
            </FormControl>
            }
            {getEnumValueByEnumName("CommunicationType") && 
            <FormControl sx={{ m: 1, minWidth: 80 }}>
              <CustomEnumSelect
                key={searchCommunicationType}
                label={t('message.common.subtitle.communication.type')}
                labelName={t('message.common.subtitle.communication.type')}
                name={'searchCommunicationType'}
                defaultValue={searchCommunicationType}
                onChange={(e)=>{
                  setSearchCommunicationType(e.target.value);
                }}
                list={getEnumValueByEnumName("CommunicationType")}
            />
            </FormControl>
            }
            <FormControl sx={{ m: 1, minWidth: 120 }}>
                <CustomEnumSelect
                    key={searchField}
                    label={t('message.common.btn.search')}
                    labelName={t('message.common.btn.search')}
                    name={'searchField'}
                    defaultValue={searchField}
                    onChange={(e)=>{
                        setSearchField(e.target.value);
                    }}
                    isLabelView={false}
                    list={searchFields}
                />
            </FormControl>

            <FormControl sx={{ m: 1 }} variant="standard">
                <BootstrapInput id="demo-customized-textbox" value={searchKeyword} onChange={handleInput} onKeyPress={handleOnKeyPress} />
            </FormControl>

            <Button variant="contained" style={{marginTop:"7px",padding:"8px 15px"}} onClick={handleSearch}>
              <span className="icon_png"><img src={`${process.env.PUBLIC_URL}/img/icon/search.png`} alt='' /></span>{t('message.common.btn.search')}
            </Button>
        </>
        }
        </Box>
      </div>

      <Box 
        id="tableDiv"
        sx={{
          mt: 3,
          padding: "20px",
          borderRadius: "20px",
          overflow: "hidden",
          background: `${mode === 'dark' ? "#182131": "#fff"}`,
          border: `${mode === 'dark' ? "1px solid rgba(255,255,255,0.06)": "1px solid #30363d"}`,
          boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
        }}
      >
        <DataGridPro
          rows={sensors && sensors !== undefined ? sensors : []}
          columns={columns}
          disableColumnFilter
          sortModel={sortModel || []}
          onSortModelChange={setSortModel}
          localeText={muiLocaleMap[i18n.language] ?? enUS.components.MuiDataGrid.defaultProps.localeText}
          sx={{
            "& .MuiDataGrid-footerContainer, & .MuiDataGrid-toolbarContainer": {
              display: "none",
            },
            "& .MuiDataGrid-cell.statusReport" : {overflow:"unset !important"}
          }}
        />

        <PageInfo pageInfo={pageInfo} handlePaging={handlePaging}/>
      </Box>
    </div>
    );
}

export default SensorSetting;